/* Cart Notification v2 — popup (desktop) / bottom sheet (mobile).
   Тег <cart-notification> сохранён намеренно: product-form.js и quick-add.js
   ищут его и вызывают getSectionsToRender / setActiveElement / renderContents.

   Строки строятся из снапшота корзины (секция ds-cart-notification-data):
   там есть метафилды и money-формат. «Сколько добавлено» — дельта снапшотов,
   а не response.quantity, семантика которого неоднозначна. */

const DS_CN_VISIBLE_ROWS = 2;

class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.panel = this.querySelector('.ds-cn__panel');
    this.backdrop = this.querySelector('.ds-cn__backdrop');
    this.rowsEl = this.querySelector('[data-cn-rows]');
    this.titleEl = this.querySelector('[data-cn-title]');
    this.liveEl = this.querySelector('[data-cn-live]');
    this.spoilerEl = this.querySelector('[data-cn-spoiler]');
    this.spoilerTextEl = this.querySelector('[data-cn-spoiler-text]');
    this.rowTemplate = this.querySelector('[data-cn-row-template]');
    this.removedTemplate = this.querySelector('[data-cn-removed-template]');

    this.timerSeconds = parseInt(this.dataset.timer, 10);
    if (isNaN(this.timerSeconds)) this.timerSeconds = 10;
    this.moneyFormat = this.dataset.moneyFormat || '{{ amount }} ₪';
    this.onCartPage = this.dataset.onCartPage === 'true';

    /** key -> сколько единиц добавлено в этой сессии показа */
    this.added = new Map();
    this.snapshot = this.readCartJSON(this) || { items: [] };
    this.expanded = false;
    this.timerStopped = false;
    this.isOpen = false;
    this.closing = false;

    this.pluralRules = this.makePluralRules();

    this.onKeydown = this.handleKeydown.bind(this);
    this.stopTimer = this.stopTimer.bind(this);

    this.querySelectorAll('[data-cn-close]').forEach((el) =>
      el.addEventListener('click', this.close.bind(this))
    );
    this.spoilerEl.addEventListener('click', this.toggleSpoiler.bind(this));

    ['pointerenter', 'focusin', 'touchstart', 'wheel'].forEach((evt) =>
      this.panel.addEventListener(evt, this.stopTimer, { passive: true })
    );

    this.setupSwipeToClose();
  }

  /* ---------- контракт с product-form.js / quick-add.js ---------- */

  getSectionsToRender() {
    return [{ id: 'ds-cart-notification-data' }, { id: 'cart-icon-bubble' }];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  renderContents(response) {
    if (this.onCartPage) return;

    const sections = response && response.sections;
    if (sections && sections['cart-icon-bubble']) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) bubble.innerHTML = this.extractSectionHTML(sections['cart-icon-bubble']);
    }

    const next = sections && sections['ds-cart-notification-data']
      ? this.readCartJSON(this.parseHTML(sections['ds-cart-notification-data']))
      : null;
    if (!next) return;

    this.accumulateAdded(this.snapshot, next);
    this.snapshot = next;

    if (this.added.size === 0) return;

    this.render();
    this.open();
  }

  /** Тихая синхронизация: степпер на карточке уже изменил корзину, снапшот
      надо подвинуть, но popup показывать не нужно — иначе «+» и «В корзину»
      говорят на двух разных языках. */
  syncSnapshot(response) {
    const html = response && response.sections && response.sections['ds-cart-notification-data'];
    const next = html ? this.readCartJSON(this.parseHTML(html)) : null;
    if (next) this.snapshot = next;
  }

  /* ---------- данные ---------- */

  parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  extractSectionHTML(html) {
    const parsed = this.parseHTML(html);
    const inner = parsed.querySelector('.shopify-section') || parsed.body;
    return inner.innerHTML;
  }

  readCartJSON(root) {
    const el = root.querySelector('[data-cn-cart]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[cart-notification] cart JSON parse failed', e);
      return null;
    }
  }

  /** Дельта количеств между снапшотами = что реально добавилось. */
  accumulateAdded(before, next) {
    const prevQty = new Map((before.items || []).map((it) => [it.key, it.quantity]));
    (next.items || []).forEach((item) => {
      const delta = item.quantity - (prevQty.get(item.key) || 0);
      if (delta > 0) this.added.set(item.key, (this.added.get(item.key) || 0) + delta);
    });
  }

  itemByKey(key) {
    return (this.snapshot.items || []).find((it) => it.key === key);
  }

  /* ---------- рендер ---------- */

  render() {
    /* Строки одной подборки (set_key) сворачиваются в одну запись-группу:
       сет показывается как один товар. Количество группы = число добавленных
       сетов (у канонического повтора дельты всех строк равны). */
    const entries = [];
    const groups = new Map();
    this.added.forEach((qty, key) => {
      const item = this.itemByKey(key);
      if (!item) return;
      if (item.set_key) {
        let group = groups.get(item.set_key);
        if (!group) {
          group = {
            group: true,
            key: item.set_key,
            title: item.set_title || item.title,
            image: item.set_image || item.image,
            qty: 0,
            totalPrice: 0,
            memberCount: 0,
            gramsCount: 0,
            gramsFirst: 0,
            gramsSame: true,
            gramsTotal: 0,
            gramsUnit: '',
            hasBox: false,
            extrasCount: 0,
          };
          groups.set(item.set_key, group);
          entries.push(group);
        }
        group.qty = Math.max(group.qty, qty);
        group.totalPrice += item.unit_price * qty;
        group.memberCount += 1;
        // Обложка подборки из метаобъекта; фолбэк — фото первой строки.
        if (item.set_image && group.image !== item.set_image) group.image = item.set_image;
        if (!group.image && item.image) group.image = item.image;
        // Роли как в корзине: чай (граммовка в названии) → сводка
        // «6 × 50 g · 300 g», коробка → префикс «В коробке», остальное —
        // счётчик аксессуаров.
        if (item.set_role === 'tea' && item.grams > 0) {
          group.gramsCount += 1;
          if (!group.gramsFirst) {
            group.gramsFirst = item.grams;
            group.gramsUnit = item.grams_unit || '';
          } else if (item.grams !== group.gramsFirst) {
            group.gramsSame = false;
          }
          group.gramsTotal += item.grams;
        } else if (item.set_role === 'box') {
          group.hasBox = true;
        } else if (item.set_role === 'extra') {
          group.extrasCount += 1;
        }
      } else {
        entries.push({ item, qty });
      }
    });

    const totalAdded = entries.reduce((sum, e) => sum + e.qty, 0);
    this.titleEl.textContent = this.pluralize('title', totalAdded, { count: totalAdded });

    const visible = this.expanded ? entries : entries.slice(0, DS_CN_VISIBLE_ROWS);
    const hidden = this.expanded ? [] : entries.slice(DS_CN_VISIBLE_ROWS);

    this.rowsEl.innerHTML = '';
    visible.forEach((entry) => this.rowsEl.appendChild(this.buildRow(entry)));

    if (hidden.length) {
      const hiddenQty = hidden.reduce((sum, e) => sum + e.qty, 0);
      const hiddenSum = hidden.reduce(
        (sum, e) => sum + (e.group ? e.totalPrice : e.item.unit_price * e.qty),
        0
      );
      this.spoilerTextEl.textContent = this.expanded
        ? this.dataset.collapse
        : this.pluralize('spoiler', hiddenQty, {
            count: hiddenQty,
            amount: this.formatMoney(hiddenSum),
          });
      this.spoilerEl.hidden = false;
    } else if (this.expanded && entries.length > DS_CN_VISIBLE_ROWS) {
      this.spoilerTextEl.textContent = this.dataset.collapse;
      this.spoilerEl.hidden = false;
    } else {
      this.spoilerEl.hidden = true;
    }

    this.renderTotals();
  }

  buildRow(entry) {
    if (entry.group) return this.buildGroupRow(entry);
    const { item, qty } = entry;
    const node = this.rowTemplate.content.cloneNode(true);
    const row = node.querySelector('.ds-cn__row');
    row.dataset.key = item.key;

    const img = node.querySelector('.ds-cn__thumb');
    if (item.image) {
      img.src = item.image;
      img.alt = item.title;
    } else {
      img.remove();
    }

    node.querySelector('[data-cn-name]').textContent = item.title;
    node.querySelector('[data-cn-meta]').textContent = item.meta || '';

    const qtyEl = node.querySelector('[data-cn-qty]');
    if (qty > 1) {
      qtyEl.textContent = `${qty} × ${this.formatMoney(item.unit_price)}`;
      qtyEl.hidden = false;
    }

    // Если добавили всю строку целиком — берём готовую сумму из Liquid.
    node.querySelector('[data-cn-price]').textContent =
      qty === item.quantity ? item.line_price_formatted : this.formatMoney(item.unit_price * qty);

    node
      .querySelector('[data-cn-remove]')
      .addEventListener('click', () => this.removeLine(item.key));

    return node;
  }

  /** Строка-группа подборки: название сета, суммарная цена, одно удаление. */
  buildGroupRow(entry) {
    const node = this.rowTemplate.content.cloneNode(true);
    const row = node.querySelector('.ds-cn__row');
    row.dataset.key = entry.key;

    const img = node.querySelector('.ds-cn__thumb');
    if (entry.image) {
      img.src = entry.image;
      img.alt = entry.title;
    } else {
      img.remove();
    }

    node.querySelector('[data-cn-name]').textContent = entry.title;

    const eyebrow = node.querySelector('[data-cn-eyebrow]');
    if (eyebrow && this.dataset.setLabel) {
      // Текст — во вложенном спане: рядом живёт скрытая иконка подарка,
      // которую показываем при выбранной подарочной коробке (hasBox).
      const eyebrowText = eyebrow.querySelector('[data-cn-eyebrow-text]');
      if (eyebrowText) eyebrowText.textContent = this.dataset.setLabel;
      else eyebrow.textContent = this.dataset.setLabel;
      // SVG не реализует свойство .hidden — только атрибут (+ явный CSS)
      const gift = eyebrow.querySelector('[data-cn-gift]');
      if (gift && entry.hasBox) gift.removeAttribute('hidden');
      eyebrow.hidden = false;
    }

    // Состав как в корзине: «В коробке: 6 × 50 g · 300 g» (сводка чая,
    // числа в bidi-изоляте — иначе RTL перемешивает) + строкой ниже
    // «Дополнительно: N аксессуаров». Фолбэк без граммовки — «6 шт.».
    const lines = [];
    if (entry.gramsCount > 0) {
      let note = String(entry.gramsCount);
      if (entry.gramsSame && entry.gramsFirst > 0) {
        note += ` × ${entry.gramsFirst} ${entry.gramsUnit}`.trimEnd();
      }
      if (entry.gramsTotal > 0) {
        note += ` · ${entry.gramsTotal} ${entry.gramsUnit}`.trimEnd();
      }
      note = `⁨${note}⁩`; // FSI…PDI: направление от первого сильного символа — RTL на иврите, LTR на RU
      if (entry.hasBox && this.dataset.setInBoxLabel) {
        note = `${this.dataset.setInBoxLabel}: ${note}`;
      }
      lines.push(note);
    } else {
      const units = this.dataset.units;
      lines.push(units ? `${entry.memberCount} ${units}` : String(entry.memberCount));
    }
    if (entry.extrasCount > 0 && this.dataset.setExtrasLabel) {
      lines.push(
        `${this.dataset.setExtrasLabel}: ⁨${entry.extrasCount}⁩ ${this.pluralize('acc', entry.extrasCount, {})}`
      );
    }
    node.querySelector('[data-cn-meta]').textContent = lines.join('\n');

    const qtyEl = node.querySelector('[data-cn-qty]');
    if (entry.qty > 1) {
      qtyEl.textContent = `${entry.qty} × ${this.formatMoney(entry.totalPrice / entry.qty)}`;
      qtyEl.hidden = false;
    }

    node.querySelector('[data-cn-price]').textContent = this.formatMoney(entry.totalPrice);

    node
      .querySelector('[data-cn-remove]')
      .addEventListener('click', () => this.removeGroup(entry.key));

    return node;
  }

  renderTotals() {
    const data = this.snapshot;
    const units = this.dataset.units;
    // display_count: подборка (чаи + коробка) = один товар; считает Liquid
    // (cart-display-count). Фолбэк на item_count для старых снапшотов.
    const count = data.display_count != null ? data.display_count : data.item_count;
    this.querySelector('[data-cn-count]').textContent = units
      ? `${count} ${units}`
      : String(count);
    this.querySelector('[data-cn-subtotal]').textContent = data.total_price_formatted;

    const ship = this.querySelector('[data-cn-ship]');
    ship.classList.remove('is-pickup', 'is-courier', 'is-free');
    ship.classList.add(`is-${data.ship_state}`);
    this.querySelector('[data-cn-ship-msg]').textContent = data.ship_message;
    this.querySelector('[data-cn-fill]').style.width = `${data.progress}%`;

    this.querySelector('[data-cn-ms-pickup]').classList.toggle('is-done', !!data.pickup_done);
    this.querySelector('[data-cn-ms-courier]').classList.toggle('is-done', !!data.courier_done);
  }

  /* ---------- действия ---------- */

  toggleSpoiler() {
    this.expanded = !this.expanded;
    this.classList.toggle('is-expanded', this.expanded);
    this.stopTimer();
    this.render();
  }

  removeLine(key) {
    const item = this.itemByKey(key);
    if (!item) return;

    const addedQty = this.added.get(key) || 0;
    const nextQty = Math.max(0, item.quantity - addedQty);
    const row = this.rowsEl.querySelector(`[data-key="${CSS.escape(key)}"]`);

    fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: key,
        quantity: nextQty,
        sections: this.getSectionsToRender().map((s) => s.id).join(','),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((cart) => {
        if (cart.sections && cart.sections['cart-icon-bubble']) {
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble) bubble.innerHTML = this.extractSectionHTML(cart.sections['cart-icon-bubble']);
        }

        const next =
          cart.sections && cart.sections['ds-cart-notification-data']
            ? this.readCartJSON(this.parseHTML(cart.sections['ds-cart-notification-data']))
            : null;
        if (next) this.snapshot = next;

        this.added.delete(key);
        this.showRemoved(row, item.title);

        document.dispatchEvent(new CustomEvent('cart:sections-rendered'));
      })
      .catch((e) => console.error('[cart-notification] remove failed', e));
  }

  /** Убрать всю подборку: откат добавленных количеств всех её строк одним
      /cart/update.js. Семантика та же, что у removeLine, но по группе. */
  removeGroup(setKey) {
    const row = this.rowsEl.querySelector(`[data-key="${CSS.escape(setKey)}"]`);
    const updates = {};
    const memberKeys = [];
    let title = '';
    (this.snapshot.items || []).forEach((it) => {
      if (it.set_key !== setKey) return;
      if (!title) title = it.set_title || it.title;
      const addedQty = this.added.get(it.key) || 0;
      if (!addedQty) return;
      updates[it.key] = Math.max(0, it.quantity - addedQty);
      memberKeys.push(it.key);
    });
    if (!memberKeys.length) return;

    fetch(window.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates,
        sections: this.getSectionsToRender().map((s) => s.id).join(','),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((cart) => {
        if (cart.status) throw cart;
        if (cart.sections && cart.sections['cart-icon-bubble']) {
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble) bubble.innerHTML = this.extractSectionHTML(cart.sections['cart-icon-bubble']);
        }

        const next =
          cart.sections && cart.sections['ds-cart-notification-data']
            ? this.readCartJSON(this.parseHTML(cart.sections['ds-cart-notification-data']))
            : null;
        if (next) this.snapshot = next;

        memberKeys.forEach((key) => this.added.delete(key));
        this.showRemoved(row, title);

        document.dispatchEvent(new CustomEvent('cart:sections-rendered'));
      })
      .catch((e) => console.error('[cart-notification] group remove failed', e));
  }

  showRemoved(row, title) {
    if (row) {
      const node = this.removedTemplate.content.cloneNode(true);
      node.querySelector('[data-cn-gone-text]').textContent = `${this.dataset.removed} · ${title}`;
      row.replaceWith(node);
    }

    setTimeout(() => {
      if (this.added.size === 0) {
        this.close();
        return;
      }
      this.render();
    }, 2000);

    if (this.added.size > 0) this.renderTotals();
  }

  /* ---------- открытие / закрытие ---------- */

  isSheet() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  open() {
    // сбрасываем инлайн-стили от драга/прошлого улёта, иначе не сыграет вход
    if (this.drag) this.drag.reset();
    this.backdrop.style.opacity = '';
    this.closing = false;

    this.hidden = false;
    this.isOpen = true;
    document.addEventListener('keydown', this.onKeydown);
    this.startTimer();

    // фокус намеренно не крадём — анонс идёт через aria-live.
    // Текст ставим после снятия hidden, иначе скринридер не озвучит.
    requestAnimationFrame(() => {
      this.liveEl.textContent = this.titleEl.textContent;
    });
  }

  /** Закрытие с анимацией: шит улетает вниз, десктопный поповер — вверх. */
  close() {
    if (this.closing || (!this.isOpen && this.hidden)) return;
    this.closing = true;
    this.clearTimer();

    const finish = () => this.finishClose();

    if (window.DSSheet) {
      window.DSSheet.flyOut(this.panel, finish, {
        up: !this.isSheet(),
        backdrop: this.isSheet() ? this.backdrop : null,
      });
    } else {
      finish();
    }
  }

  finishClose() {
    this.hidden = true;
    this.isOpen = false;
    this.closing = false;
    this.added.clear();
    this.expanded = false;
    this.classList.remove('is-expanded');
    this.liveEl.textContent = '';
    // следующее уведомление — новая сессия, таймер снова активен
    this.timerStopped = false;
    this.classList.remove('is-timer-off', 'is-timer-running');
    document.removeEventListener('keydown', this.onKeydown);
    if (this.drag) this.drag.reset();
    this.backdrop.style.opacity = '';
  }

  handleKeydown(event) {
    if (event.key === 'Escape') this.close();
  }

  /* ---------- таймер ---------- */

  startTimer() {
    this.clearTimer();
    const ring = this.querySelector('[data-cn-ring-svg]');

    if (this.timerStopped || this.timerSeconds <= 0) {
      this.classList.add('is-timer-off');
      if (ring) ring.hidden = this.timerSeconds <= 0;
      return;
    }

    this.classList.remove('is-timer-off');
    if (ring) ring.hidden = false;
    this.style.setProperty('--cn-duration', `${this.timerSeconds}s`);

    // рестарт CSS-анимации кольца
    this.classList.remove('is-timer-running');
    void this.offsetWidth;
    this.classList.add('is-timer-running');

    this.timerId = setTimeout(() => this.close(), this.timerSeconds * 1000);
  }

  stopTimer() {
    if (this.timerStopped) return;
    this.timerStopped = true;
    this.clearTimer();
    this.classList.remove('is-timer-running');
    this.classList.add('is-timer-off');
  }

  clearTimer() {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  /* ---------- mobile: перетаскивание шита ---------- */

  setupSwipeToClose() {
    if (!window.DSSheet) return;

    this.drag = window.DSSheet.dragToDismiss(this.panel, {
      isEnabled: () => this.isSheet() && this.isOpen && !this.closing,
      onDismiss: () => this.close(),
      // бэкдроп гаснет пропорционально протяжке — шит «отрывается» от экрана
      onDrag: (delta) => {
        const progress = Math.min(1, Math.max(0, delta) / Math.max(this.panel.offsetHeight, 1));
        this.backdrop.style.opacity = String(1 - progress * 0.7);
      },
    });
  }

  /* ---------- форматирование ---------- */

  makePluralRules() {
    try {
      return new Intl.PluralRules(document.documentElement.lang || 'ru');
    } catch (e) {
      return null;
    }
  }

  /** Локали держат три формы: one / few / many. two|other сводим к many. */
  pluralize(prefix, count, replacements) {
    const category = this.pluralRules ? this.pluralRules.select(count) : 'other';
    const suffix = category === 'one' ? 'one' : category === 'few' ? 'few' : 'many';
    const key = `${prefix}${suffix.charAt(0).toUpperCase()}${suffix.slice(1)}`;
    let text = this.dataset[key] || this.dataset[`${prefix}Many`] || '';
    Object.keys(replacements || {}).forEach((name) => {
      const token = name === 'count' ? '@@N@@' : '@@SUM@@';
      text = text.split(token).join(replacements[name]);
    });
    return text;
  }

  formatMoney(cents) {
    let amount = (cents / 100).toFixed(2);
    const noDecimals = String(Math.round(cents / 100));
    if (amount.slice(-3) === '.00') amount = noDecimals;
    return this.moneyFormat
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  }
}

if (!customElements.get('cart-notification')) {
  customElements.define('cart-notification', CartNotification);
}
